/**
 * M05 Prompt 管理 fixture · S3 Day12
 */
import type {
	DPrimeTemplateResponse,
	MainAgentTemplateResponse,
	PromptABConfigResponse,
	PromptSandboxResponse,
	PromptVersionListResponse,
} from "@/lib/api/prompts";
import type {
	AgentRuntimeConfig,
	AgentRuntimeConfigPutRequest,
	WriteActionItem,
	WriteActionListResponse,
} from "@/lib/api/runtime";

import { registerFixture } from "./registry";

let dPrimeTemplate = `<prefetched_evidence>
  <auto_invoked_results>
{{auto_invoked_results}}
  </auto_invoked_results>
  <rag_snippets>
{{rag_snippets}}
  </rag_snippets>
</prefetched_evidence>`;

let mainAgentTemplate = `# 主 Agent 基准提示词 m05-v1

你是主 Agent（多轮对话 Agent），人格设定是 ENFJ，热情、专业、善解人意且靠谱。

用户当前输入：{{current_user_message}}
召回方案：{{playbook_context}}
本轮允许使用的 Skills：{{allowed_skills}}
d' 预召回证据区：{{d_prime_context}}

处理规则：unknown/no_match 不等于自动转人工；先尝试回应、知识回答或追问，只有明确需要人工时才升级。

工具规则：启用 SDK loop 时，真实可调用函数由 allowed_skills 与 SkillManifest.skill_id 交集决定，并以 call_SKxx wrapper 暴露；只读补查受预算和去重约束，SK11 不注册为 function tool，写类工具不代表 live 写入。

输出合约：{{output_contract}}`;

const updatedAt = new Date("2026-05-01T00:00:00.000Z").toISOString();
let runtimeConfig: AgentRuntimeConfig = {
	merchant_id: "mer_devcons",
	sdk_loop_enabled: true,
	max_steps: 6,
	quality_mode: "q_deep_side_effect",
	write_mode: "live_confirmed",
	live_write_requires_confirmation: true,
	live_write_requires_q_deep: true,
	live_write_mutation_direct_from_agent: false,
	updated_by: "system",
	notes: "P11 default",
	updated_at: updatedAt,
};

let pendingWriteActions: WriteActionItem[] = [
	{
		audit_id: "wa_mock_001",
		merchant_id: "mer_devcons",
		session_id: "session_mock_001",
		trace_id: "trace_mock_001",
		customer_id: "cust_mock",
		skill_id: "SK07",
		tool_id: "cancel_order",
		idempotency_key: "cancel-1001-v1",
		order_id: "gid://shopify/Order/1001",
		status: "pending_confirmation",
		write_mode: "plan_only",
		confirmation_status: "pending_user_confirmation",
		confirmation_plan: {
			skill_id: "SK07",
			tool_id: "cancel_order",
			write_mode: "plan_only",
			mutation_executed: false,
			audit_id: "wa_mock_001",
		},
		dry_run_action: { kind: "write_simulated" },
		q_gate: {},
		live_result: null,
		mutation_executed: false,
		confirmed_by: null,
		confirmed_at: null,
		rejected_by: null,
		rejected_at: null,
		reject_reason: null,
		created_at: updatedAt,
		updated_at: updatedAt,
	},
	{
		audit_id: "wa_mock_002",
		merchant_id: "mer_devcons",
		session_id: "session_mock_002",
		trace_id: "trace_mock_002",
		customer_id: "cust_mock",
		skill_id: "SK09",
		tool_id: "refund_order",
		idempotency_key: "refund-1002-v1",
		order_id: "gid://shopify/Order/1002",
		status: "rejected",
		write_mode: "plan_only",
		confirmation_status: "rejected",
		confirmation_plan: {
			skill_id: "SK09",
			tool_id: "refund_order",
			write_mode: "plan_only",
			mutation_executed: false,
			audit_id: "wa_mock_002",
		},
		dry_run_action: { kind: "write_simulated", amount: "10.00" },
		q_gate: { passed: false, issues: ["operator_rejected"] },
		live_result: null,
		mutation_executed: false,
		confirmed_by: null,
		confirmed_at: null,
		rejected_by: "ops-reviewer",
		rejected_at: updatedAt,
		reject_reason: "客户未确认退款金额",
		created_at: updatedAt,
		updated_at: updatedAt,
	},
	{
		audit_id: "wa_mock_003",
		merchant_id: "mer_devcons",
		session_id: "session_mock_003",
		trace_id: "trace_mock_003",
		customer_id: "cust_mock",
		skill_id: "SK10",
		tool_id: "update_shipping_address",
		idempotency_key: "address-1003-v1",
		order_id: "gid://shopify/Order/1003",
		status: "live_executed",
		write_mode: "live_confirmed",
		confirmation_status: "confirmed",
		confirmation_plan: {
			skill_id: "SK10",
			tool_id: "update_shipping_address",
			write_mode: "plan_only",
			mutation_executed: false,
			audit_id: "wa_mock_003",
		},
		dry_run_action: { kind: "write_simulated", address1: "100 Main St" },
		q_gate: { passed: true, issues: [] },
		live_result: { status: "success_complete" },
		mutation_executed: true,
		confirmed_by: "ops-reviewer",
		confirmed_at: updatedAt,
		rejected_by: null,
		rejected_at: null,
		reject_reason: null,
		created_at: updatedAt,
		updated_at: updatedAt,
	},
];

function estimateTokens(text: string): number {
	return Math.max(1, text.trim().split(/\s+/).filter(Boolean).length);
}

function emptyWriteAction(auditId: string): WriteActionItem {
	return {
		audit_id: auditId,
		merchant_id: runtimeConfig.merchant_id,
		session_id: null,
		trace_id: null,
		customer_id: null,
		skill_id: "SK07",
		tool_id: "cancel_order",
		idempotency_key: "missing",
		order_id: null,
		status: "rejected",
		write_mode: "plan_only",
		confirmation_status: "rejected",
		confirmation_plan: {},
		dry_run_action: {},
		q_gate: {},
		live_result: null,
		mutation_executed: false,
		confirmed_by: null,
		confirmed_at: null,
		rejected_by: "mock-admin",
		rejected_at: new Date().toISOString(),
		reject_reason: "not found",
		created_at: updatedAt,
		updated_at: new Date().toISOString(),
	};
}

function versionList(): PromptVersionListResponse {
	return {
		total: 2,
		items: [
			{
				version_id: "m05-v1",
				title: "M05 default prompt · d-prime evidence first",
				status: "published",
				region_count: 10,
				main_agent_token_estimate: estimateTokens(mainAgentTemplate),
				d_prime_token_estimate: estimateTokens(dPrimeTemplate),
				updated_by: "system",
				updated_at: updatedAt,
			},
			{
				version_id: "m05-v2",
				title: "M05 sandbox candidate",
				status: "draft",
				region_count: 10,
				main_agent_token_estimate: estimateTokens(mainAgentTemplate) + 10,
				d_prime_token_estimate: estimateTokens(dPrimeTemplate) + 3,
				updated_by: "pm@example.com",
				updated_at: new Date().toISOString(),
			},
		],
	};
}

export function registerM05Fixtures(): void {
	registerFixture<PromptVersionListResponse>({
		pathname: "/admin/v1/prompts/versions",
		methods: ["GET"],
		resolve: () => ({ data: versionList() }),
	});

	registerFixture<DPrimeTemplateResponse>({
		pathname: "/admin/v1/prompts/prompt-templates/d-prime",
		methods: ["GET"],
		resolve: () => ({
			data: {
				version_id: "m05-v1",
				template: dPrimeTemplate,
				variables: ["auto_invoked_results", "rag_snippets"],
				token_estimate: estimateTokens(dPrimeTemplate),
				updated_by: "system",
				updated_at: updatedAt,
			},
		}),
	});

	registerFixture<MainAgentTemplateResponse>({
		pathname: "/admin/v1/prompts/prompt-templates/main-agent",
		methods: ["GET"],
		resolve: () => ({
			data: {
				version_id: "m05-v1",
				template: mainAgentTemplate,
				variables: [
					"allowed_skills",
					"customer_profile",
					"current_user_message",
					"d_prime_context",
					"history_summary",
					"intent_context",
					"output_contract",
					"playbook_context",
					"seasonal_context",
					"tool_runtime_context",
				],
				token_estimate: estimateTokens(mainAgentTemplate),
				updated_by: "system",
				updated_at: updatedAt,
			},
		}),
	});

	registerFixture<MainAgentTemplateResponse>({
		pathname: "/admin/v1/prompts/prompt-templates/main-agent",
		methods: ["PATCH"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as { template?: string; version_id?: string };
			mainAgentTemplate = body.template ?? mainAgentTemplate;
			return {
				data: {
					version_id: body.version_id ?? "m05-v1",
					template: mainAgentTemplate,
					variables: [
						"allowed_skills",
						"customer_profile",
						"current_user_message",
						"d_prime_context",
						"history_summary",
						"intent_context",
						"output_contract",
						"playbook_context",
						"seasonal_context",
						"tool_runtime_context",
					],
					token_estimate: estimateTokens(mainAgentTemplate),
					updated_by: "mock-admin",
					updated_at: new Date().toISOString(),
				},
			};
		},
	});

	registerFixture<DPrimeTemplateResponse>({
		pathname: "/admin/v1/prompts/prompt-templates/d-prime",
		methods: ["PATCH"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as { template?: string; version_id?: string };
			dPrimeTemplate = body.template ?? dPrimeTemplate;
			return {
				data: {
					version_id: body.version_id ?? "m05-v1",
					template: dPrimeTemplate,
					variables: ["auto_invoked_results", "rag_snippets"],
					token_estimate: estimateTokens(dPrimeTemplate),
					updated_by: "mock-admin",
					updated_at: new Date().toISOString(),
				},
			};
		},
	});

	registerFixture<PromptABConfigResponse>({
		pathname: "/admin/v1/prompts/merchants/:merchant_id/system_prompt",
		methods: ["GET"],
		resolve: (ctx) => ({
			data: {
				merchant_id: ctx.params.merchant_id ?? "mer_nY0bZmLI",
				system_prompt_version_a: "m05-v1",
				system_prompt_version_b: null,
				system_prompt_ab_ratio: 0,
				updated_at: updatedAt,
			},
		}),
	});

	registerFixture<PromptABConfigResponse>({
		pathname: "/admin/v1/prompts/merchants/:merchant_id/system_prompt",
		methods: ["PUT"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as {
				system_prompt_version_a?: string;
				system_prompt_version_b?: string | null;
				system_prompt_ab_ratio?: number;
			};
			return {
				data: {
					merchant_id: ctx.params.merchant_id ?? "mer_nY0bZmLI",
					system_prompt_version_a: body.system_prompt_version_a ?? "m05-v1",
					system_prompt_version_b: body.system_prompt_version_b ?? null,
					system_prompt_ab_ratio: body.system_prompt_ab_ratio ?? 0,
					updated_at: new Date().toISOString(),
				},
			};
		},
	});

	registerFixture<AgentRuntimeConfig>({
		pathname: "/admin/v1/runtime/merchants/:merchant_id/agent",
		methods: ["GET"],
		resolve: (ctx) => ({
			data: {
				...runtimeConfig,
				merchant_id: ctx.params.merchant_id ?? runtimeConfig.merchant_id,
			},
		}),
	});

	registerFixture<AgentRuntimeConfig>({
		pathname: "/admin/v1/runtime/merchants/:merchant_id/agent",
		methods: ["PUT"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as AgentRuntimeConfigPutRequest;
			runtimeConfig = {
				...runtimeConfig,
				merchant_id: ctx.params.merchant_id ?? runtimeConfig.merchant_id,
				sdk_loop_enabled: body.sdk_loop_enabled,
				max_steps: body.max_steps,
				quality_mode: body.quality_mode,
				write_mode: body.write_mode,
				updated_by: body.updated_by ?? "mock-admin",
				notes: body.notes ?? null,
				updated_at: new Date().toISOString(),
			};
			return { data: runtimeConfig };
		},
	});

	registerFixture<WriteActionListResponse>({
		pathname: "/admin/v1/write-actions",
		methods: ["GET"],
		resolve: (ctx) => {
			const status = ctx.query.status;
			const items =
				status && status !== "all"
					? pendingWriteActions.filter((item) => item.status === status)
					: pendingWriteActions;
			return { data: { items, total: items.length } };
		},
	});

	registerFixture<WriteActionItem>({
		pathname: "/admin/v1/write-actions/:audit_id/confirm",
		methods: ["POST"],
		resolve: (ctx) => {
			const auditId = ctx.params.audit_id ?? "";
			const body = (ctx.body ?? {}) as { confirmed_by?: string };
			pendingWriteActions = pendingWriteActions.map((item) =>
				item.audit_id === auditId
					? {
							...item,
							status: "live_executed",
							confirmation_status: "confirmed",
							mutation_executed: true,
							q_gate: { passed: true, issues: [] },
							live_result: { status: "success_complete" },
							confirmed_by: body.confirmed_by ?? "mock-admin",
							confirmed_at: new Date().toISOString(),
							updated_at: new Date().toISOString(),
						}
					: item,
			);
			return {
				data:
					pendingWriteActions.find((item) => item.audit_id === auditId) ??
					emptyWriteAction(auditId),
			};
		},
	});

	registerFixture<WriteActionItem>({
		pathname: "/admin/v1/write-actions/:audit_id/reject",
		methods: ["POST"],
		resolve: (ctx) => {
			const auditId = ctx.params.audit_id ?? "";
			const body = (ctx.body ?? {}) as { rejected_by?: string; reason?: string | null };
			pendingWriteActions = pendingWriteActions.map((item) =>
				item.audit_id === auditId
					? {
							...item,
							status: "rejected",
							confirmation_status: "rejected",
							rejected_by: body.rejected_by ?? "mock-admin",
							rejected_at: new Date().toISOString(),
							reject_reason: body.reason ?? "mock rejected",
							updated_at: new Date().toISOString(),
						}
					: item,
			);
			return {
				data:
					pendingWriteActions.find((item) => item.audit_id === auditId) ??
					emptyWriteAction(auditId),
			};
		},
	});

	registerFixture<PromptSandboxResponse>({
		pathname: "/admin/v1/prompts/sandbox",
		methods: ["POST"],
		resolve: () => ({
			delay_ms: 120,
			data: {
				prompt_version: "m05-v1",
				sandbox_only: true,
				main_agent_prompt_preview: mainAgentTemplate
					.replace("{{current_user_message}}", "帮我查一下订单 #1001 的物流状态")
					.replace("{{playbook_context}}", "sandbox preview · PB01_order_query")
					.replace("{{allowed_skills}}", "SK01, SK02, SK05, SK11")
					.replace("{{d_prime_context}}", "<d_prime>prefetched_evidence</d_prime>")
					.replace("{{output_contract}}", "<response>...</response>"),
				d_prime_preview: dPrimeTemplate
					.replace(
						"{{auto_invoked_results}}",
						'    <skill id="SK01" status="failed">Shopify PCD blocked order lookup</skill>',
					)
					.replace(
						"{{rag_snippets}}",
						'    <rag namespace="policy" doc_id="shipping-policy" score="0.910">物流查询需订单读取权限。</rag>',
					),
				token_estimate: 18,
				phase_preview: [
					{ phase: "phase1_refine", status: "ok", summary: { slot_keys: ["order_id"] } },
					{ phase: "phase2_recall", status: "sandbox", summary: { live_tools_executed: false } },
					{ phase: "phase3_rerank", status: "sandbox", summary: { evidence_count: 2 } },
					{
						phase: "phase4_generate",
						status: "preview",
						summary: { action_type_hint: "escalate" },
					},
					{ phase: "phase5_validate", status: "preview", summary: { decision_hint: "PASS" } },
				],
			},
		}),
	});
}
